import React from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import useParams from '@/hooks/useParams';
import { useUserContext } from '@/modules/auth/hooks/useUser';
import ContentAccessEvents from '@/modules/corporate/components/ContentAccessEvents';
import ContentViewEvents from '@/modules/corporate/components/ContentViewEvents';
import Loader from '@/modules/corporate/components/CourseLoader';
import ErrorAccess from '@/modules/corporate/components/ErrorAccess';
import FaceVerification from '@/modules/corporate/components/FaceVerification';
import useContentPerformance from '@/modules/corporate/hooks/useContentPerformance';
import { ArticleContextProvider } from '@/modules/corporate/modules/article/articleContext';
import useArticleActions from '@/modules/corporate/modules/article/hooks/useArticleActions';
import useResourcePerformanceResponse from '@/modules/corporate/modules/course/views/CourseDetails/hooks/useResourcePerformanceResponse';
import { useAdminRoute } from '@/pages/corporate/routes/hooks/useAdminRoutes';

import Body from './Body';
import { BodyConatinerProps } from './types';

const BodyContainer: React.FC<BodyConatinerProps> = (props) => {
  const {
    articleId,
    articleData,
    articleBlocks,
    isPreviewing,
    previewRootBlockId,
    refetch,
    isDetailsPage,
    journeySessionId,
    hideAccessories,
  } = props;

  const { articleContent, onAction } = useArticleActions({
    article: articleData,
    articleId,
    data: articleBlocks,
  });
  const { isLocalizedContent } = articleData;

  const user = useUserContext();
  const [pauseAvtivity, { toggle: setPauseActivity }] = useToggle(true);
  const isAdminRoute = useAdminRoute();
  const { parentEntityId: pathId } = useParams<{ parentEntityId: string }>();
  const entityIds = pathId ? [articleId, pathId] : [articleId];
  const { data: contentPerformances } = useContentPerformance(
    entityIds,
    user.id,
    false,
    isAdminRoute,
    isLocalizedContent,
  );
  const pathPerformance = useResourcePerformanceResponse(pathId, contentPerformances);
  if (!contentPerformances?.[articleId] && !isAdminRoute && !isPreviewing) {
    return <Loader />;
  }
  if (
    !isAdminRoute &&
    !isPreviewing &&
    contentPerformances?.[articleId] &&
    !contentPerformances?.[articleId].isAvailable
  ) {
    // contentPerformance is not present in case of preview.
    return <ErrorAccess resource="article" />;
  }

  return (
    <>
      <ContentViewEvents
        entityId={articleId}
        pause={isPreviewing || pauseAvtivity}
        performanceStatus={articleData.performance?.status}
      />
      <ContentAccessEvents
        entityId={articleId}
        pause={isPreviewing || pauseAvtivity}
        performanceStatus={articleData.performance?.status}
        recalibrationRequired={articleData.performance?.recalibrationRequired}
      />
      {pathPerformance ? ( // To increase the access count of parent path
        <ContentAccessEvents
          entityId={pathId}
          pause={isPreviewing || pauseAvtivity}
          performanceStatus={pathPerformance?.status}
          recalibrationRequired={false}
        />
      ) : null}
      <ArticleContextProvider
        article={articleData}
        articleContent={articleContent}
        hideAccessories={hideAccessories}
        isDetailsPage={isDetailsPage}
        isPreviewing={isPreviewing}
        journeySessionId={journeySessionId}
        pauseAvtivity={pauseAvtivity}
        previewRootBlockId={previewRootBlockId}
        onAction={onAction}>
        <Body />
      </ArticleContextProvider>
      <FaceVerification entityId={articleId} setPauseActivity={setPauseActivity} />
    </>
  );
};

export default React.memo(BodyContainer);
